<template>
    <modal
        :active="true"
        @closed="$emit('closed', true)"
        :title="title()"
        :large="true"
    >
        <SearchItems
            v-if="isStepSearch"
            :currency="currency"
            @selected="onSelectItem"
        />

        <DefaultFields v-if="isStepFields"
                       :currency="currency"
                       :error="error"
                       :form="form"
                       :max-count="maxAvailableItems"
                       :is-goods="isGoods"
                       :total-price="totalPrice"
                       :editable-manufacturer-name="editableFields.manufacturerName"
                       :editable-type="editableFields.type"
                       :editable-description="editableFields.description"
                       :editable-count="editableFields.count"
                       :editable-serial="editableFields.serial"
                       :editable-deliver="editableFields.deliver"
                       :editable-price="editableFields.price"
                       :editable-discount="editableFields.discount"
                       :editable-shipping="editableFields.shipping"
                       :editable-origin="editableFields.origin"
                       :editable-customs-code="editableFields.customs_code"
                       :editable-weight="editableFields.weight"
        />

        <template v-slot:footer>
            <div class="footer-spacer">
                <div v-if="isStepSearch">
                    <button class="button is-default" @click="gotoStepFields">Manuel indtastning</button>
                </div>
                <div v-if="isStepFields">
                    <button class="button is-danger is-flex-left" @click="reset">Nulstil</button>
                    <button class="button is-default" @click="$emit('closed', true)">Annuller</button>
                    <button class="button is-primary" @click="submit">{{ saveButtonText }}</button>
                </div>
            </div>
        </template>
    </modal>
</template>
<script>
import Modal from '@elems/modal'
import {FormField, InputText, InputCheckbox, InputSelect, InputStatic, InputDate, InputDecimal} from '@elems/form'
import SearchItems from "./forms/SearchItems";
import DefaultFields from "./forms/DefaultFields";
import ItemPreview from "./forms/ItemPreview";
import {mapActions, mapGetters} from "vuex";

export default {
    components: {
        ItemPreview,
        DefaultFields,
        SearchItems,
        Modal,
        FormField,
        InputText,
        InputSelect,
        InputCheckbox,
        InputStatic,
        InputDate,
        InputDecimal,
    },
    props: {
        active: Boolean,
        forProject: {
            type: Boolean,
            default: true
        },
        parent: Object,
        customer: Object,
        lineId: {
            default: 0,
            type: Number
        },
        currency: {
            type: String,
            default: 'DKK'
        },
    },
    data() {
        return {
            activeStep: 0,
            maxAvailableItems: null,
            form: {
                type: '',
                description: '',
                serial: '',
                count: 1,
                deliver_at: '',
                price: 0,
                discount: 0,
                no_shipping: 0,
                customs_code: '',
                weight: 0,
                country_id: null,
                manufacturer_name: null,
            },
            error: [],
            selected: {
                item: null,
                price: null,
                serial: null,
                location: null,
            },
        }
    },
    computed: {
        ...mapGetters([
            'settingByKey',
        ]),
        editableFields() {
            return {
                type: this.itemType === null,
                description: true,
                count: this.itemType === null || this.itemType === 'no_serial' || this.itemType === 'service',
                serial: this.itemType === null || (this.itemType === 'with_serial' && this.line.serial === null),
                deliver: this.itemType === null,
                manufacturerName: this.itemType === null,
                price: true,
                customs_code: true,
                weight: true,
                origin: true,
                discount: true,
                shipping: true,
            };
        },
        isEdit() {
            return this.lineId > 0
        },
        isGoods() {
            return !this.isEdit || (this.line.service_ref === undefined || this.line.service_ref === null)
        },
        line() {
            if (this.lineId === 0 || !this.parent.lines.length) {
                return {}
            }

            return this.parent.lines.find(item => item.id === this.lineId)
        },
        saveButtonText() {
            return this.isEdit ? 'Gem' : 'Opret'
        },
        totalPrice() {
            const discount = 1 - (this.form.discount / 100)
            return (this.form.count * (this.form.price / 100) * discount).toFixed(2)
        },
        apiUrl() {
            const part = this.forProject ? 'project' : 'report'
            return `/api/${part}s/${this.parent.id}/${part}-lines`
        },
        itemType() {
            if (this.isEdit) {
                if (this.line.item_type === null) {
                    return null;
                }

                return this.line.item_type.key
            }
            if (this.selected.item === null) {
                return null;
            }

            return this.selected.item.type.key;
        },
        isStepSearch() {
            return this.activeStep === 0;
        },
        isStepFields() {
            return this.activeStep === 1;
        }
    },
    methods: {
        ...mapActions([
            'loadSettings',
        ]),
        gotoStepSearch() {
            this.activeStep = 0;
        },
        gotoStepFields() {
            this.activeStep = 1;
        },
        getPostRequestPayload() {
            let rules = {};

            const defaultRules = {
                discount: this.form.discount,
                deliver_at: this.form.deliver_at,
                no_shipping: this.form.no_shipping,
                item_type_key: null,
                country_id: this.form.country_id,
            }

            if (this.itemType === 'no_serial') {
                rules = {
                    count: this.form.count,
                    item_id: this.selected.item.id,
                    item_location_id: this.selected.location.id,
                    price_id: this.selected.price.id,
                    item_type_key: this.selected.item.type.key,
                    description: this.form.description,
                    country_id: this.form.country_id,
                    price: this.form.price,
                    weight: this.form.weight,
                    customs_code: this.form.customs_code,
                }
            }

            if (this.itemType === 'with_serial') {
                rules = {
                    item_id: this.selected.item.id,
                    serial_id: this.selected.serial.id,
                    price_id: this.selected.price.id,
                    item_type_key: this.selected.item.type.key,
                    description: this.form.description,
                    country_id: this.form.country_id,
                    price: this.form.price,
                    weight: this.form.weight,
                    customs_code: this.form.customs_code,
                }
            }

            if (this.itemType === 'service') {
                rules = {
                    count: this.form.count,
                    item_id: this.selected.item.id,
                    price_id: this.selected.price.id,
                    item_type_key: this.selected.item.type.key,
                    description: this.form.description,
                    country_id: this.form.country_id,
                    price: this.form.price,
                    weight: this.form.weight,
                    customs_code: this.form.customs_code,
                }
            }

            if (this.itemType === null) {
                rules = {
                    count: this.form.count,
                    type: this.form.type,
                    description: this.form.description,
                    serial: this.form.serial,
                    price: this.form.price,
                    customs_code: this.form.customs_code,
                    weight: this.form.weight,
                    manufacturer_name: this.form.manufacturer_name,
                }
            }

            return { ...defaultRules, ...rules };
        },
        onSelectItem(data) {
            this.selected.item = data.item;
            this.selected.price = data.price;
            this.selected.location = data.location;
            this.selected.serial = data.serial;

            this.form.discount = this.getDiscount();

            if (this.itemType === 'with_serial') {
                this.form.serial = data.serial.serial;
            }

            this.form.item_id = data.item.id;
            this.form.type = data.item.model;
            this.form.manufacturer_name = data.item?.manufacturer?.name || null;
            this.form.description = this.currency === 'DKK' ? data.item.description_da_raw : data.item.description_en_raw;
            this.form.weight = data.item.weight || null;
            this.form.country_id = data.item.country_id || null;
            this.form.customs_code = data.item.customs_code || null;

            if (this.currency === 'EUR') {
                const EUR = this.settingByKey('inventory.exchange.rate').value / 100;
                this.form.price = Math.round((data.price.price / 100) * (100 / EUR));
            } else {
                this.form.price = data.price.price;
            }

            this.activeStep = 1;
            this.calculateMaxAvailableItems();
        },
        title() {
            return this.isEdit ? 'Rediger linje' : 'Opret linje'
        },
        submit() {
            return this.isEdit ? this.update() : this.create()
        },
        create() {
            const requestPayload = this.getPostRequestPayload();

            window.axios.post(`${this.apiUrl}`, requestPayload)
                .then(({ data }) => {
                    this.$emit('closed')
                    this.reset()
                    this.$emit('created', data)
                    this.$emit('completed', data)
                    this.$store.dispatch('setSuccess', 'Linjen blev oprettet')
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                    }
                })
        },
        getDiscount() {
            let discount = 0;
            if (this.customer) {
                if (this.customer.global_product_discount !== null && this.itemType !==null && this.customer.global_product_discount > 0) {
                    discount = this.customer.global_product_discount;
                } else if (this.selected.item) {
                    discount = Math.min(this.customer.product_discount, this.selected.item.max_discount);
                } else {
                    discount = this.customer.product_discount;
                }
            }

            return discount;
        },
        update() {
            if (this.itemType === 'with_serial' && this.line.serial === null && this.form.serial !== null) {
                if (!confirm('Serienummer feltet låses, tryk på OK for at fortsætte.')) {
                    return
                }
            }

            if (this.itemType === 'with_serial' && this.form.price < 0) {
                if (!confirm('Ved kreditering af serienummer bliver det tilføjes på Returlisten - ønsker du at fortsætte?')) {
                    return;
                }
            }

            window.axios.put(this.apiUrl + '/' + this.lineId, this.form)
                .then(({ data }) => {
                    this.$emit('closed')
                    this.$emit('updated', data)
                    this.$emit('completed', data)
                    this.$store.dispatch('setSuccess', 'Linjen blev opdateret')
                    this.reset()
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                    }
                })
        },
        reset() {
            if (this.isEdit) {
                this.form = {
                    type: this.line.type,
                    description: this.line.description,
                    serial: this.line.serial,
                    count: this.line.count,
                    deliver_at: this.line.deliver_at,
                    price: this.line.price,
                    discount: this.line.discount,
                    no_shipping: this.line.no_shipping,
                    customs_code: this.line.customs_code,
                    weight: this.line.weight,
                    country_id: this.line.country_id,
                    manufacturer_name: this.line.manufacturer_name,
                }
                this.activeStep = 1;
            } else {

                const discount = this.getDiscount();

                this.form = {
                    type: '',
                    description: '',
                    serial: '',
                    count: 1,
                    deliver_at: '',
                    price: 0,
                    discount: discount,
                    no_shipping: 0,
                    customs_code: '',
                    weight: 0,
                    country_id: null,
                    manufacturer_name: null,
                }
                this.maxAvailableItems = null;

                this.activeStep = 0;

                this.selected = {
                    item: null,
                    price: null,
                    serial: null,
                    location: null,
                }
            }

            this.error = [];
        },
        calculateMaxAvailableItems() {
            if (this.itemType === 'no_serial') {
                const item = this.isEdit ? this.line.item : this.selected.location;

                this.maxAvailableItems = this.isEdit ? item.available + this.form.count : item.available;
                return;
            }

            this.maxAvailableItems = null;
        },
    },
    created() {
        this.loadSettings();
        this.reset();
        if (this.isEdit) {
            this.gotoStepFields();
        }

        this.calculateMaxAvailableItems();
    },
}
</script>

<style lang="scss">
.icon {
    margin-right: 10px;
}
</style>
